import { ApolloClient } from '@apollo/client';
import {
    SAVE_SPLIT_ROUTE_CACHE,
    GET_SPLIT_ROUTE_CACHE,
    DELETE_SPLIT_ROUTE_CACHE,
    BATCH_GET_SPLIT_ROUTE_CACHE,
    SaveSplitRouteCacheResponse,
    GetSplitRouteCacheResponse,
    DeleteSplitRouteCacheResponse,
    BatchGetSplitRouteCacheResponse,
} from '../graphql/routePlanningCache';
import {
    type OrderMatchItem,
    matchOrderIdsByPolygons,
    matchOrderIdsByZipcodes,
    matchOrderIdsByStopRanges,
} from './orderMatchUtil';

const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

// assignType 枚举（与 assignDrivers.ts 的 AssignType 一致）
const AT_STOP = 1;
const AT_ZIPCODE = 2;
const AT_MAP = 3;
const AT_ALGORITHM = 4;

// ─── Apollo Client 注入 ───
let _client: ApolloClient<any> | null = null;

/**
 * 注入 Apollo Client（在 App 初始化时调用一次）
 */
export const initSplitRouteCacheClient = (client: ApolloClient<any>) => {
    _client = client;
};

// ─── localStorage 工具（Redis 失败时兜底） ───

const localKey = (warehouseId: number, areaId: number) => `splitRoute_${warehouseId}_${areaId}`;

const loadFromLocal = (warehouseId: number, areaId: number): Record<string, any> | null => {
    const raw = localStorage.getItem(localKey(warehouseId, areaId));
    if (!raw) return null;
    try {
        const parsed = JSON.parse(raw);
        if (parsed.savedAt && Date.now() - parsed.savedAt > CACHE_TTL_MS) {
            localStorage.removeItem(localKey(warehouseId, areaId));
            return null;
        }
        return parsed;
    } catch {
        return null;
    }
};

const saveToLocal = (warehouseId: number, areaId: number, data: Record<string, any>) => {
    localStorage.setItem(localKey(warehouseId, areaId), JSON.stringify({ ...data, savedAt: Date.now() }));
};

const removeLocal = (warehouseId: number, areaId: number) => {
    localStorage.removeItem(localKey(warehouseId, areaId));
};

// ─── 对外接口（异步，Redis 优先 + localStorage 兜底） ───

/**
 * 保存缓存（Redis 优先，失败降级 localStorage）
 */
export const saveSplitRouteCache = async (warehouseId: number, areaId: number, data: Record<string, any>): Promise<void> => {
    const dataWithTimestamp = { ...data, savedAt: Date.now() };
    if (_client) {
        try {
            await _client.mutate<SaveSplitRouteCacheResponse>({
                mutation: SAVE_SPLIT_ROUTE_CACHE,
                variables: { warehouseId, areaId, data: JSON.stringify(dataWithTimestamp) },
            });
        } catch (e) {
            console.warn('[splitRouteCache] save to Redis failed, fallback to localStorage', e);
            saveToLocal(warehouseId, areaId, dataWithTimestamp);
        }
    } else {
        saveToLocal(warehouseId, areaId, dataWithTimestamp);
    }
};

/**
 * 读取缓存（Redis 优先，localStorage 只读兜底）
 */
export const loadSplitRouteCache = async (warehouseId: number, areaId: number): Promise<Record<string, any> | null> => {
    // 1. 先查 Redis
    if (_client) {
        try {
            const { data: result } = await _client.query<GetSplitRouteCacheResponse>({
                query: GET_SPLIT_ROUTE_CACHE,
                variables: { warehouseId, areaId },
                fetchPolicy: 'network-only',
            });
            if (result?.getSplitRouteCache) {
                return JSON.parse(result.getSplitRouteCache);
            }
        } catch (e) {
            console.warn('[splitRouteCache] load from Redis failed, fallback to localStorage', e);
        }
    }
    // 2. 回退 localStorage（只读，不迁移）
    return loadFromLocal(warehouseId, areaId);
};

/**
 * 删除缓存（同时删 Redis + localStorage）
 */
export const deleteSplitRouteCache = async (warehouseId: number, areaId: number): Promise<void> => {
    removeLocal(warehouseId, areaId);
    if (_client) {
        try {
            _client.mutate<DeleteSplitRouteCacheResponse>({
                mutation: DELETE_SPLIT_ROUTE_CACHE,
                variables: { warehouseId, areaId },
            });
        } catch (e) {
            console.warn('[splitRouteCache] delete from Redis failed', e);
        }
    }
};

/**
 * 批量读取缓存（Redis 优先，localStorage 只读兜底）
 */
export const batchLoadSplitRouteCache = async (
    warehouseId: number,
    areaIds: number[]
): Promise<Record<number, Record<string, any>>> => {
    const result: Record<number, Record<string, any>> = {};
    if (areaIds.length === 0) return result;

    // 1. 批量查 Redis
    if (_client) {
        try {
            const { data: batchResult } = await _client.query<BatchGetSplitRouteCacheResponse>({
                query: BATCH_GET_SPLIT_ROUTE_CACHE,
                variables: { warehouseId, areaIds },
                fetchPolicy: 'network-only',
            });
            (batchResult?.batchGetSplitRouteCache ?? []).forEach(item => {
                if (item.data) {
                    try {
                        result[item.areaId] = JSON.parse(item.data);
                    } catch { /* skip */ }
                }
            });
        } catch (e) {
            console.warn('[splitRouteCache] batchGet from Redis failed, fallback to localStorage', e);
        }
    }

    // 2. Redis 没命中的从 localStorage 只读兜底（不迁移）
    for (const areaId of areaIds) {
        if (result[areaId]) continue;
        const localData = loadFromLocal(warehouseId, areaId);
        if (localData) {
            result[areaId] = localData;
        }
    }

    return result;
};

/**
 * 从后端已发布的 childTaskList 构建缓存数据
 * 用于主页面修改已发布分段司机时，创建缓存使 Drawer 能感知修改
 *
 * map/zipcode/stop 模式：用 assignTypeDetail（分配规则）+ 当前实时 orderList 匹配 assignedOrderIds，与 Drawer 逻辑一致
 * algorithm 模式：用 routePlanningOrderList 快照 ∩ 当前 orderList，过滤已不在池中的订单
 *
 * @param assignType 发布时的分单模式
 * @param children 后端返回的子任务列表
 * @param areaName 区域名称
 * @param areaId 区域ID
 * @param orderList 当前实时订单列表，用于匹配/过滤 assignedOrderIds
 */
export const buildCacheFromApiChildren = (
    assignType: number,
    children: any[],
    areaName: string,
    areaId?: string | number | null,
    orderList?: OrderMatchItem[],
): Record<string, any> | null => {
    if (!children.length) return null;

    const orders = orderList ?? [];
    const currentOrderIdSet = new Set(orders.map(o => Number(o.orderId)).filter(id => !isNaN(id) && id > 0));

    const buildBaseSeg = (child: any, idx: number) => ({
        id: areaId ? `${areaId}-${child.dspTaskId ?? child.id ?? idx}` : String(child.dspTaskId ?? child.id ?? idx),
        name: child.areaName ?? child.taskName ?? `${areaName}-${idx + 1}`,
        colorIndex: idx,
        driverAccountId: child.driverId ? String(child.driverId) : '',
        assignedOrderIds: [] as number[],
        totalParcelCount: child.totalParcels ?? child.totalParcelCount ?? 0,
        parcelNumberStart: child.parcelNumberStart ?? null,
        parcelNumberEnd: child.parcelNumberEnd ?? null,
        startAddress: child.startAddress ?? null,
        endAddress: child.endAddress ?? null,
    });

    if (assignType === AT_MAP) {
        const mapAssigned = new Set<number>();
        return {
            lastSplitMode: 'map',
            map: children.map((child, idx) => {
                const polygons = (child.assignTypeDetail ?? []).filter(Boolean).map((fenceStr: string) =>
                    fenceStr.split(';').map(s => {
                        const [lat, lng] = s.split(',').map(Number);
                        return { lat, lng };
                    })
                );
                const assignedOrderIds = matchOrderIdsByPolygons(orders, polygons, mapAssigned);
                assignedOrderIds.forEach(id => mapAssigned.add(id));
                const seg = buildBaseSeg(child, idx);
                return { ...seg, polygons, assignedOrderIds, totalParcelCount: assignedOrderIds.length };
            }),
            zipcode: [],
            stop: [],
        };
    }

    if (assignType === AT_ZIPCODE) {
        const zipAssigned = new Set<number>();
        return {
            lastSplitMode: 'zipcode',
            map: [],
            zipcode: children.map((child, idx) => {
                const zipcodes = child.assignTypeDetail ?? [];
                const assignedOrderIds = matchOrderIdsByZipcodes(orders, zipcodes).filter(id => !zipAssigned.has(id));
                assignedOrderIds.forEach(id => zipAssigned.add(id));
                const seg = buildBaseSeg(child, idx);
                return { ...seg, zipcodes, assignedOrderIds, totalParcelCount: assignedOrderIds.length };
            }),
            stop: [],
        };
    }

    if (assignType === AT_STOP) {
        return {
            lastSplitMode: 'stop',
            map: [],
            zipcode: [],
            stop: children.map((child, idx) => {
                const details = child.assignTypeDetail ?? [];
                const stopRanges = details.length > 0
                    ? details.map((detail: string, rangeIdx: number) => {
                        const sep = detail.includes(',') ? ',' : '-';
                        const [start = '', end = ''] = detail.split(sep);
                        return { id: `range-${child.dspTaskId ?? idx}-${rangeIdx}`, parcelNumberStart: start, parcelNumberEnd: end };
                    })
                    : [{
                        id: `range-${child.dspTaskId ?? idx}`,
                        parcelNumberStart: child.parcelNumberStart != null ? String(child.parcelNumberStart) : '',
                        parcelNumberEnd: child.parcelNumberEnd != null ? String(child.parcelNumberEnd) : '',
                    }];
                const assignedOrderIds = matchOrderIdsByStopRanges(orders, stopRanges);
                const seg = buildBaseSeg(child, idx);
                return { ...seg, stopRanges, assignedOrderIds, totalParcelCount: assignedOrderIds.length };
            }),
        };
    }

    if (assignType === AT_ALGORITHM) {
        const stopMap: Record<string, number> = {};
        const segments = children.map((child, idx) => {
            // 快照 orderIds ∩ 当前 orderList，过滤已领走的包裹
            const snapshotOrderIds = (child.routePlanningOrderList ?? []).map((o: any) => Number(o.orderId));
            const orderIds = orders.length > 0
                ? snapshotOrderIds.filter((id: number) => currentOrderIdSet.has(id))
                : snapshotOrderIds;
            (child.routePlanningOrderList ?? []).forEach((o: any) => {
                if (o.orderId != null && o.stopNumber != null) {
                    stopMap[String(o.orderId)] = o.stopNumber;
                }
            });
            const detail = child.assignTypeDetail ?? [];
            const first = detail[0];
            const hasCapacity = first && /^\d+,\d+$/.test(first);
            const [min, max] = hasCapacity ? first.split(',').map(Number) : [null, null];
            const seg = buildBaseSeg(child, idx);
            return {
                ...seg,
                assignedOrderIds: orderIds,
                totalParcelCount: orderIds.length,
                capacityMin: min,
                capacityMax: max,
                preferredZipcodes: hasCapacity ? detail.slice(1) : detail,
                algoRecordId: null,
            };
        });
        return {
            lastSplitMode: 'algo',
            map: [],
            zipcode: [],
            stop: [],
            algo: {
                phase: 'published',
                segments,
                stopMap: Object.keys(stopMap).length > 0 ? stopMap : undefined,
            },
        };
    }

    return null;
};
